export type CircuitBreakerState = 'closed' | 'open' | 'half_open';

export type CircuitBreakerConfig = {
  readonly enabled: boolean;
  readonly failureThreshold: number;
  readonly resetTimeoutMs: number;
  readonly halfOpenMaxAttempts: number;
};

export type CircuitBreakerMetrics = {
  readonly state: CircuitBreakerState;
  readonly consecutiveFailures: number;
  readonly totalFailures: number;
  readonly totalSuccesses: number;
  readonly totalBlocked: number;
  readonly lastFailureAt: number | null;
  readonly lastSuccessAt: number | null;
  readonly openedAt: number | null;
};

export class CircuitBreaker {
  private readonly config: CircuitBreakerConfig;

  private state: CircuitBreakerState = 'closed';

  private consecutiveFailures = 0;

  private totalFailures = 0;

  private totalSuccesses = 0;

  private totalBlocked = 0;

  private halfOpenAttempts = 0;

  private lastFailureAt: number | null = null;

  private lastSuccessAt: number | null = null;

  private openedAt: number | null = null;

  constructor(config: CircuitBreakerConfig) {
    this.config = config;
  }

  canExecute(): boolean {
    if (!this.config.enabled) return true;

    if (this.state === 'closed') return true;

    if (this.state === 'open') {
      if (
        this.openedAt !== null &&
        Date.now() - this.openedAt >= this.config.resetTimeoutMs
      ) {
        this.state = 'half_open';
        this.halfOpenAttempts = 0;
        return true;
      }

      this.totalBlocked += 1;
      return false;
    }

    return this.halfOpenAttempts < this.config.halfOpenMaxAttempts;
  }

  recordSuccess(): void {
    if (!this.config.enabled) return;

    this.totalSuccesses += 1;
    this.lastSuccessAt = Date.now();
    this.consecutiveFailures = 0;
    this.halfOpenAttempts = 0;
    this.state = 'closed';
    this.openedAt = null;
  }

  recordFailure(): void {
    if (!this.config.enabled) return;

    this.totalFailures += 1;
    this.lastFailureAt = Date.now();
    this.consecutiveFailures += 1;

    if (this.state === 'half_open') {
      this.halfOpenAttempts += 1;
      if (this.halfOpenAttempts >= this.config.halfOpenMaxAttempts) {
        this.open();
      }
      return;
    }

    if (this.consecutiveFailures >= this.config.failureThreshold) {
      this.open();
    }
  }

  forceOpen(): void {
    this.open();
  }

  forceClose(): void {
    this.state = 'closed';
    this.consecutiveFailures = 0;
    this.halfOpenAttempts = 0;
    this.openedAt = null;
  }

  getMetrics(): CircuitBreakerMetrics {
    return {
      state: this.state,
      consecutiveFailures: this.consecutiveFailures,
      totalFailures: this.totalFailures,
      totalSuccesses: this.totalSuccesses,
      totalBlocked: this.totalBlocked,
      lastFailureAt: this.lastFailureAt,
      lastSuccessAt: this.lastSuccessAt,
      openedAt: this.openedAt,
    };
  }

  private open(): void {
    this.state = 'open';
    this.openedAt = Date.now();
  }
}
